import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index";

// Lazy-load all non-homepage routes to reduce initial JS bundle
const AllServices = lazy(() => import("./pages/AllServices"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogPostPPFEverything = lazy(() => import("./pages/BlogPostPPFEverything"));
const Contact = lazy(() => import("./pages/Contact"));
const Training = lazy(() => import("./pages/Training"));
const AutomotiveService = lazy(() => import("./pages/AutomotiveService"));
const PPFService = lazy(() => import("./pages/PPFService"));
const CeramicCoatingService = lazy(() => import("./pages/CeramicCoatingService"));
const CustomWrapsService = lazy(() => import("./pages/CustomWrapsService"));
const CommercialService = lazy(() => import("./pages/CommercialService"));
const ResidentialService = lazy(() => import("./pages/ResidentialService"));
const DecorativeFilmService = lazy(() => import("./pages/DecorativeFilmService"));
const CountertopApplianceService = lazy(() => import("./pages/CountertopApplianceService"));
const MarineService = lazy(() => import("./pages/MarineService"));

const DetailingService = lazy(() => import("./pages/DetailingService"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<AllServices />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/ppf-for-everything-you-value" element={<BlogPostPPFEverything />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/training" element={<Training />} />
            
            
            {/* Dedicated PPF & Color PPF Pages */}
            <Route path="/automotive/ppf" element={<PPFService />} />
            <Route path="/automotive/ceramic" element={<CeramicCoatingService />} />
            <Route path="/automotive/wraps" element={<CustomWrapsService />} />
            <Route path="/automotive/detailing" element={<DetailingService />} />
            
            {/* Other Automotive Services */}
            <Route path="/automotive/:service" element={<AutomotiveService />} />
            
            {/* Commercial Services */}
            <Route path="/commercial/:service" element={<CommercialService />} />

            
            {/* Residential Services */}
            <Route path="/residential/countertop" element={<CountertopApplianceService />} />
            <Route path="/residential/decorative" element={<DecorativeFilmService />} />
            <Route path="/residential/tint" element={<Navigate to="/commercial/tint#residential" replace />} />
            
            {/* Marine & Aviation Services */}
            <Route path="/marine/ppf" element={<MarineService />} />
            <Route path="/marine/ceramic" element={<MarineService />} />
            
            {/* Legacy redirects */}
            <Route path="/mobile-detailing" element={<Navigate to="/automotive/detailing" replace />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
